import React, { KeyboardEventHandler, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Breakpoints } from '../../types/Breakpoints';
import { Icons } from '../../components/atoms/SVG';
import { Namespace } from '../../translation/namespace';
import { InstitutionDisplayBlock, isInstitutionDisplayBlock } from '../../_blocks/InstitutionDisplay';
import { InstantSearchBlock, isInstantSearchBlock } from '../../_blocks/InstantSearch';
import { isLoginLogoutNoScriptBlock, LoginLogoutNoScriptBlock } from '../../_blocks/LoginLogout';
import { useAppContext } from '../../contexts/AppContext/AppContext';
import { HeaderCollapsible } from '../../_lib/components/HeaderCollapsible';
import { useGlobalMenu, GlobalMenuState } from '../../contexts/GlobalMenuStore';
import { setFocusToTarget } from './HeaderSection.utils';
import { KeyEvent } from '../../types/KeyEvent';
import { GlobalDocumentIds } from '../../types/GlobalDocumentIds';

type HeaderBlocks = InstitutionDisplayBlock | InstantSearchBlock | LoginLogoutNoScriptBlock;
export type HeaderSection = {
  blocks?: HeaderBlocks[];
  uuid: string;
  showToggles?: true;
};
export const HeaderSection = memo(
  function ({ showToggles, blocks = [] }: HeaderSection) {
    HeaderSection.displayName = 'HeaderSection';
    // keep blocks in state due to rerender on SPA navigation
    // footer section is omitted and will be empty after page is fetched
    const institutionDisplay = blocks.find(isInstitutionDisplayBlock);
    const instantSearch = blocks.find(isInstantSearchBlock);
    const loginLogout = blocks.find(isLoginLogoutNoScriptBlock);

    const { userInfo, windowInnerWidth } = useAppContext();
    const getPalIcon = () => {
      if (userInfo && userInfo.loggedIn) {
        return Icons.USER_PAL;
      }
      return windowInnerWidth < Breakpoints.m_smartphone_landscape ? Icons.CLOSED_LOCK : Icons.USER_PAL;
    };
    const { t } = useTranslation(Namespace.HeaderSection);

    const { currentOpenMenu } = useGlobalMenu();

    const focusElement =
      (menuType: GlobalMenuState, targetId: GlobalDocumentIds) => (event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (currentOpenMenu === menuType && event.code === KeyEvent.Tab && !event.shiftKey) {
          event.preventDefault();
          setFocusToTarget(targetId, event.target as HTMLElement);
        }
      };
    const focusNavigation: KeyboardEventHandler<HTMLButtonElement> = focusElement(
      GlobalMenuState.MAIN_NAVIGATION,
      GlobalDocumentIds.mainNavigationBox,
    );
    const focusPal: KeyboardEventHandler<HTMLButtonElement> = focusElement(
      GlobalMenuState.PAL,
      GlobalDocumentIds.palContainer,
    );

    return (
      <header role="banner" className="header" aria-labelledby="header-headline">
        <h2 className="invisible" id="header-headline">
          {t('headline')}
        </h2>
        <div className="header__container">
          {showToggles && (
            <HeaderCollapsible
              id={GlobalDocumentIds.mainNavigationBox}
              title={t('navigationBox.title')}
              modifier="navigation"
              icon={Icons.HAMBURGER_MENU}
              aria-haspopup
              onKeyDown={focusNavigation}
            />
          )}
          {institutionDisplay && <InstitutionDisplayBlock {...institutionDisplay} />}
          {instantSearch && <InstantSearchBlock {...instantSearch} />}
          {loginLogout && <LoginLogoutNoScriptBlock {...loginLogout} />}
          {showToggles && (
            <HeaderCollapsible
              id={GlobalDocumentIds.personalBox}
              title={t('personalBox.title')}
              modifier="personal"
              icon={getPalIcon()}
              onKeyDown={focusPal}
            />
          )}
        </div>
        <div className="header__brandingcolor-bar" />
      </header>
    );
  },
  (prevProps, nextProps) => prevProps.uuid === nextProps.uuid,
);
